import * as React from "react";
import MenuItemModel from "../models/MenuItemModel";
import HomeIcon from "@material-ui/icons/Home";
import SettingsIcon from "@material-ui/icons/Settings";
import AssessmentIcon from "@material-ui/icons/Assessment";
import HelpIcon from "../../components/HelpIcon";
import DocumentIcon from "@material-ui/icons/LibraryBooks";
import SearchIcon from "@material-ui/icons/Search";
import FilterHdr from "@material-ui/icons/FilterHdr";
import { SessionCache } from "../../helpers/cache";
import User from "../models/User";
import { isArrayValid } from "../../helpers/utility";
import {
  PERMISSION_ACCESS_ADMIN,
  PERMISSION_ACCESS_BASE,
  PERMISSION_ACCESS_CONSUMPTION_CHART,
  PERMISSION_ACCESS_CONSUMPTION_REPORT,
  PERMISSION_ACCESS_INFORM_LEVEL,
  PERMISSION_ACCESS_LEVEL_CHART,
  PERMISSION_ACCESS_LEVEL_REPORT,
  PERMISSION_ACCESS_ALL_INSTALLATIONS,
  PermissionsType,
} from "../models/Permission";
import CustomerModel from "../models/Customer";
import { useHubx } from "../../HubX";
import { FETCH_CUSTOMERS } from "../../Fedx";
import useConstructor from "./useConstructor";
import InstallationGroup from "../models/InstallationGroup";
import get from "lodash/get";

interface UseAppLinks {
  menu: MenuItemModel[];
  getFirstOrDefaultInstallationDetailRoute: (path: string, permission: PermissionsType) => string;
}

const useAppLinks = (): UseAppLinks => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cache = new SessionCache();

  const [customers, setCustomer] = React.useState<null | CustomerModel[]>(() => {
    const cachedCustomers: null | CustomerModel[] = cache.get("customers");
    return cachedCustomers;
  });

  const hub = useHubx();

  useConstructor(async () => {
    if (customers?.every(e => e?.installations?.length)) {
      return;
    }

    const customersResult = await hub.request(FETCH_CUSTOMERS);
    setCustomer(customersResult);
  });

  const getFirstOrDefaultInstallationDetailRoute = React.useCallback(
     (path: string, permission: PermissionsType) => {
      const cachedUser: User | null = cache.get("user");

      let firstCustomerWithValidGroup:
        | InstallationGroup
        | CustomerModel
        | undefined = cachedUser?.installationGroup?.find(
        (group) =>
          isArrayValid(group.installationList) &&
          Boolean(group.permissionList?.find((x) => x.type === permission))
      );

      if (customers) {
        let rtuFilter = true;
        firstCustomerWithValidGroup = customers.find((group) => {
          return (
            isArrayValid(
              group?.installations?.filter((groupFilter) => {
                return groupFilter.has_rtu;
              })
            ) &&
            cachedUser?.installationGroup?.find((radixGroup) => {
              return (
                radixGroup.id == group.customer_id &&
                Boolean(
                  radixGroup.permissionList?.find(
                    (radixGroupFilter) => radixGroupFilter.type === permission
                  )
                )
              );
            })
          );
        });

        if (!firstCustomerWithValidGroup) {
          firstCustomerWithValidGroup = customers.find((x) => {
            return (
              isArrayValid(x?.installations) &&
              cachedUser?.installationGroup?.find((group) => {
                return (
                  group.id == x.customer_id &&
                  Boolean(group.permissionList?.find((x) => x.type === permission))
                );
              })
            );
          });
          rtuFilter = false;
        }

        if (firstCustomerWithValidGroup) {
          const firstInstallation = rtuFilter
            ? firstCustomerWithValidGroup?.installations?.find((x) => x.has_rtu)
            : get(firstCustomerWithValidGroup, "installations[0]");
          if (firstInstallation) {
            return `/installation/${firstCustomerWithValidGroup.customer_id}/${firstInstallation?.installation_id}/${path}/`;
          }
        }
      } else {
        if (firstCustomerWithValidGroup) {
          const firstInstallation = firstCustomerWithValidGroup.installationList[0];
          return `/installation/${firstCustomerWithValidGroup.id}/${firstInstallation?.installationId}/${path}/`;
        }
      }
      return "/installation/";
    },
    [cache, customers]
  );

  const menu: MenuItemModel[] = [
    {
      name: "Home",
      route: "/home/",
      icon: <HomeIcon />,
      permission: PERMISSION_ACCESS_BASE,
      subMenu: [
        {
          name: "Níveis",
          route: getFirstOrDefaultInstallationDetailRoute("level", PERMISSION_ACCESS_LEVEL_CHART),
          icon: <AssessmentIcon />,
          permission: PERMISSION_ACCESS_LEVEL_CHART,
        },
        {
          name: "Consumo",
          route: getFirstOrDefaultInstallationDetailRoute(
            "consumption",
            PERMISSION_ACCESS_CONSUMPTION_CHART
          ),
          icon: <AssessmentIcon />,
          permission: PERMISSION_ACCESS_CONSUMPTION_CHART,
        },
        {
          name: "Informar nível",
          route: getFirstOrDefaultInstallationDetailRoute(
            "send_level",
            PERMISSION_ACCESS_INFORM_LEVEL
          ),
          icon: <AssessmentIcon />,
          permission: PERMISSION_ACCESS_INFORM_LEVEL,
        },
      ],
    },
    {
      name: "Relatórios",
      route: "/reports/",
      icon: <AssessmentIcon />,
      permission: [PERMISSION_ACCESS_LEVEL_REPORT, PERMISSION_ACCESS_CONSUMPTION_REPORT],
      subMenu: [
        {
          name: "Níveis",
          route: "/reports/level/",
          icon: <DocumentIcon />,
          permission: PERMISSION_ACCESS_LEVEL_REPORT,
        },
        {
          name: "Consumo",
          route: "/reports/consumption/",
          icon: <DocumentIcon />,
          permission: PERMISSION_ACCESS_CONSUMPTION_REPORT,
        },
      ],
    },
    {
      name: "Admin",
      route: "/admin/",
      icon: <SettingsIcon />,
      permission: PERMISSION_ACCESS_ADMIN,
      subMenu: [
        {
          name: "Consultar Usuários",
          route: "/search/user",
          icon: <SearchIcon />,
          permission: PERMISSION_ACCESS_ADMIN,
        },
        {
          name: "Consultar Instalação",
          route: "/search/installation",
          icon: <SearchIcon />,
          permission: PERMISSION_ACCESS_ADMIN,
        },
      ],
    },
    {
      name: "Todas as instalações",
      route: "/all-installations/",
      icon: <FilterHdr />,
      permission: PERMISSION_ACCESS_ALL_INSTALLATIONS,
    },
    {
      name: "FAQ",
      route: "/faq/",
      icon: <HelpIcon />,
      permission: PERMISSION_ACCESS_BASE,
    },
  ];

  return { menu, getFirstOrDefaultInstallationDetailRoute };
};

export default useAppLinks;
